/**
 * `/connect/teams/callback?code=…&state=…` — where Microsoft sends a customer after they
 * sign in from the owner's Teams link. Public: they usually have no Help Genie account.
 * The signed `state` names the genie; finishing connects their organization to it and
 * installs the bot for them, so they go straight to the chat.
 */
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2 } from "lucide-react";
import { chatChannelService } from "@/services/agent/ChatChannelService";
import { TeamsInstallResult } from "@/types/agent/chat-channels";

const BRAND_GRADIENT = "linear-gradient(135deg, #4E9CFF, #FF4DB8)";
const TEAMS_REDIRECT_DELAY_MS = 800;
const LINK_BUTTON = "mt-6 inline-flex items-center min-h-[44px] px-5 py-2.5 rounded-xl bg-white font-semibold text-genie-primary";
const LINK_SECONDARY = "mt-3 inline-flex items-center min-h-[44px] px-3 text-sm font-semibold text-white underline underline-offset-4";

type ConnectState =
  | { status: "connecting" }
  | { status: "done"; result: TeamsInstallResult }
  | { status: "needs_admin" }
  | { status: "cancelled" }
  | { status: "failed"; message: string };

function initialState(code: string | null, state: string | null, error: string | null, description: string | null): ConnectState {
  if (code && state) return { status: "connecting" };
  // Microsoft's sign-in reports an organization's consent rules as an error back to this page.
  if (error === "consent_required" || description?.includes("AADSTS90094") || description?.includes("AADSTS65001")) {
    return { status: "needs_admin" };
  }
  return { status: "cancelled" };
}

export const TeamsInstallCallback = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const [connect, setConnect] = useState<ConnectState>(() =>
    initialState(code, state, searchParams.get("error"), searchParams.get("error_description")),
  );
  // The code is single-use; StrictMode's double effect would spend it twice.
  const started = useRef(false);

  useEffect(() => {
    if (!code || !state || started.current) return;
    started.current = true;

    chatChannelService
      .completeTeamsInstall(code, state)
      .then((result) => setConnect({ status: "done", result }))
      .catch((error: Error) =>
        setConnect({ status: "failed", message: error.message || "Connecting didn't finish. Please try the link again." }),
      );
  }, [code, state]);

  const chatUrl = connect.status === "done" && connect.result.installed ? connect.result.chatUrl : null;
  useEffect(() => {
    if (!chatUrl) return;
    const timer = window.setTimeout(() => window.location.assign(chatUrl), TEAMS_REDIRECT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [chatUrl]);

  return (
    <div
      className="min-h-[100dvh] flex flex-col items-center justify-center text-center px-6"
      style={{ background: BRAND_GRADIENT }}
    >
      <Helmet>
        <meta name="robots" content="noindex" />
        <title>Connect to Teams · Help Genie</title>
      </Helmet>

      {connect.status === "connecting" && (
        <>
          <Loader2 className="w-10 h-10 text-white animate-spin mb-5" />
          <h1 className="text-white text-xl font-semibold">Bringing your genie to Teams&hellip;</h1>
          <p className="text-white/85 text-sm mt-2 max-w-sm">This only takes a moment.</p>
        </>
      )}

      {connect.status === "done" && connect.result.installed && (
        <>
          <h1 className="text-white text-2xl font-bold">
            {connect.result.genieName} is in {connect.result.workspaceName || "your Teams"}
          </h1>
          <p className="text-white/85 text-sm mt-2 max-w-sm">
            {connect.result.genieName} is waiting for you in Teams. Opening your chat&hellip;
          </p>
          <a href={connect.result.chatUrl} className={LINK_BUTTON}>
            Open Teams
          </a>
          <p className="text-white/85 text-sm mt-6 max-w-sm">
            In a channel or group chat, add Help Genie and @mention it in each message.
          </p>
        </>
      )}

      {connect.status === "done" && !connect.result.installed && (
        <>
          <h1 className="text-white text-2xl font-bold">One more click</h1>
          <p className="text-white/85 text-sm mt-2 max-w-sm">
            {connect.result.genieName} is connected to {connect.result.workspaceName || "your organization"}. Add the Help
            Genie app to start chatting. If Teams says it's blocked, ask your Teams admin to allow it.
          </p>
          <a href={connect.result.addUrl} className={LINK_BUTTON}>
            Add to Teams
          </a>
          <a href={connect.result.chatUrl} className={LINK_SECONDARY}>
            Already added? Open chat
          </a>
        </>
      )}

      {connect.status === "needs_admin" && (
        <>
          <h1 className="text-white text-2xl font-bold">Your admin needs to approve this</h1>
          <p className="text-white/85 text-sm mt-2 max-w-sm">
            Your organization requires a Microsoft admin to approve Help Genie. Send them the link you were given. Once
            they&rsquo;ve opened it and approved, open it again yourself.
          </p>
        </>
      )}

      {connect.status === "cancelled" && (
        <>
          <h1 className="text-white text-2xl font-bold">Nothing was connected</h1>
          <p className="text-white/85 text-sm mt-2 max-w-sm">
            The Microsoft sign-in was cancelled. Open the link you were sent again whenever you&rsquo;re ready.
          </p>
        </>
      )}

      {connect.status === "failed" && (
        <>
          <h1 className="text-white text-2xl font-bold">We couldn&rsquo;t finish connecting</h1>
          <p className="text-white/85 text-sm mt-2 max-w-sm">{connect.message}</p>
        </>
      )}
    </div>
  );
};

export default TeamsInstallCallback;
